import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { api } from '../../../services/api';
import type { AgentVersionSummary } from '../../../types/agent-studio';
import { AgentStatusPill } from '../../../components/agent-studio';

export interface VersionsTabProps {
  systemName: string;
}

function formatDate(iso: string): string {
  const d = new Date(iso);
  const months = ['янв', 'фев', 'мар', 'апр', 'май', 'июн', 'июл', 'авг', 'сен', 'окт', 'ноя', 'дек'];
  return `${d.getDate()} ${months[d.getMonth()]} ${d.getFullYear()}`;
}

function VersionSkeleton() {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
      {[0, 1, 2].map(i => (
        <div key={i} className="card" style={{ padding: '16px 20px', opacity: 0.4, height: '68px' }} />
      ))}
    </div>
  );
}

export function VersionsTab({ systemName }: VersionsTabProps) {
  const navigate = useNavigate();
  const [versions, setVersions] = useState<AgentVersionSummary[] | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    async function fetchVersions() {
      try {
        setLoading(true);
        setError(null);
        const res = await api.listAgentVersions(systemName);
        if (!cancelled) {
          const sorted = [...res.versions].sort(
            (a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime(),
          );
          setVersions(sorted);
        }
      } catch (err) {
        if (!cancelled) setError(err instanceof Error ? err.message : String(err));
      } finally {
        if (!cancelled) setLoading(false);
      }
    }
    fetchVersions();
    return () => { cancelled = true; };
  }, [systemName]);

  if (loading) return <VersionSkeleton />;

  if (error) {
    return (
      <div className="card" style={{ padding: '24px', borderLeft: '3px solid var(--red)' }}>
        <div className="t-eyebrow" style={{ color: 'var(--red)' }}>Ошибка загрузки версий</div>
        <p className="t-body-sm" style={{ marginTop: '8px', color: 'var(--ink-secondary)' }}>{error}</p>
      </div>
    );
  }

  if (!versions || versions.length === 0) {
    return (
      <div className="card" style={{ padding: '24px', textAlign: 'center' }}>
        <p className="t-body" style={{ color: 'var(--ink-secondary)' }}>Версии не найдены.</p>
      </div>
    );
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
      {versions.map(v => {
        const isActive = v.status === 'active';
        return (
          <div
            key={v.id}
            className="card"
            onClick={() => navigate(`/agents/${systemName}/versions/${v.id}`)}
            style={{
              padding: '16px 20px',
              cursor: 'pointer',
              borderLeft: isActive ? '3px solid var(--navy)' : '3px solid transparent',
              display: 'flex',
              alignItems: 'center',
              gap: '16px',
              transition: 'border-color 0.15s ease',
            }}
            onMouseEnter={e => { e.currentTarget.style.borderColor = 'var(--navy)'; }}
            onMouseLeave={e => { e.currentTarget.style.borderColor = isActive ? 'var(--navy)' : 'transparent'; }}
          >
            <AgentStatusPill status={v.status} />
            <span style={{ fontFamily: 'var(--font-mono)', fontSize: 14, fontWeight: 600, color: 'var(--ink-primary)' }}>
              v{v.version_number}
            </span>
            <span style={{ fontSize: 13, color: 'var(--ink-secondary)' }}>{formatDate(v.created_at)}</span>
            <span style={{ fontSize: 13, color: 'var(--ink-tertiary)' }}>{v.created_by}</span>
            <span style={{ marginLeft: 'auto', fontSize: 18, color: 'var(--ink-tertiary)' }}>›</span>
          </div>
        );
      })}
    </div>
  );
}
